import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Toolbar,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { classesAPI } from '../../services/api';

export default function AdminClasses() {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    standard: '',
    display_name: '',
    sort_order: 0,
    is_active: 1,
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'success' });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      setLoading(true);
      const response = await classesAPI.getAll();
      setClasses(response.data);
    } catch (error) {
      showSnackbar('Error loading classes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, type = 'success') => {
    setSnackbar({ open: true, message, type });
  };

  const handleOpenDialog = (classItem = null) => {
    if (classItem) {
      setEditingId(classItem.id);
      setFormData({
        standard: classItem.standard,
        display_name: classItem.display_name,
        sort_order: classItem.sort_order,
        is_active: classItem.is_active,
      });
    } else {
      setEditingId(null);
      setFormData({
        standard: '',
        display_name: '',
        sort_order: 0,
        is_active: 1,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'sort_order' || name === 'is_active' ? parseInt(value) : value,
    });
  };

  const handleSave = async () => {
    if (!formData.standard || !formData.display_name) {
      showSnackbar('Standard and Display Name are required', 'error');
      return;
    }

    try {
      if (editingId) {
        await classesAPI.update(editingId, formData);
        showSnackbar('Class updated successfully', 'success');
      } else {
        await classesAPI.create(formData);
        showSnackbar('Class created successfully', 'success');
      }
      fetchClasses();
      handleCloseDialog();
    } catch (error) {
      showSnackbar('Error saving class', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this class?')) return;

    try {
      await classesAPI.delete(id);
      showSnackbar('Class deleted successfully', 'success');
      fetchClasses();
    } catch (error) {
      showSnackbar('Error deleting class', 'error');
    }
  };

  const filteredClasses = classes.filter(
    (item) =>
      item.standard.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.display_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ padding: 2 }}>
      <Toolbar sx={{ paddingLeft: 0, paddingRight: 0, marginBottom: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ marginRight: 2 }}
        >
          Add Class
        </Button>

        <TextField
          placeholder="Search by standard or name..."
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />
      </Toolbar>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>Standard</TableCell>
                <TableCell>Display Name</TableCell>
                <TableCell align="right">Sort Order</TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredClasses.length > 0 ? (
                filteredClasses.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>{item.standard}</TableCell>
                    <TableCell>{item.display_name}</TableCell>
                    <TableCell align="right">{item.sort_order}</TableCell>
                    <TableCell align="center">{item.is_active ? '✓' : '✗'}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(item)}
                        title="Edit"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(item.id)}
                        title="Delete"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ padding: 3 }}>
                    No classes found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Class' : 'Add New Class'}</DialogTitle>
        <DialogContent sx={{ paddingTop: 2 }}>
          <TextField
            fullWidth
            label="Standard"
            name="standard"
            value={formData.standard}
            onChange={handleInputChange}
            placeholder="e.g., 5, 6, 7"
            sx={{ marginBottom: 2 }}
          />
          <TextField
            fullWidth
            label="Display Name"
            name="display_name"
            value={formData.display_name}
            onChange={handleInputChange}
            placeholder="e.g., Class 5"
            sx={{ marginBottom: 2 }}
          />
          <TextField
            fullWidth
            label="Sort Order"
            name="sort_order"
            type="number"
            value={formData.sort_order}
            onChange={handleInputChange}
            sx={{ marginBottom: 2 }}
          />
          <TextField
            fullWidth
            label="Active"
            name="is_active"
            select
            value={formData.is_active}
            onChange={handleInputChange}
            SelectProps={{
              native: true,
            }}
          >
            <option value={1}>Active</option>
            <option value={0}>Inactive</option>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.type}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
